/**
 * ELF-CIRCLE — Our Story (design artboard `#2b`).
 *
 * A sticky chapter rail on the left drives a single detail pane on the right.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';

interface Chapter {
  key: string;
  head: string;
  body: string;
  tag: string;
}

const CHAPTERS: Chapter[] = [
  {
    key: '01 — Grassroots',
    head: 'From the ground up.',
    body:
      'Grassroots marketing taught us that goodwill isn’t bought — it’s earned in a room, a scene, a community, before it’s ever earned in a market. Every media buy we’ve made since has been built on that same foundation: reach without resonance is just noise.',
    tag: 'Reach without resonance is just noise.',
  },
  {
    key: '02 — Institutions',
    head: 'Into the room.',
    body:
      'Producing and marketing for prestigious halls — Lincoln Center in New York, NJPAC in New Jersey, the Detroit Institute of Arts — taught us how culture is curated, not just promoted. Institutional-grade audiences don’t respond to hype; they respond to relevance, craft, and timing.',
    tag: 'Curated, not just promoted.',
  },
  {
    key: '03 — Digital',
    head: 'Onto the platform.',
    body:
      'As media buying moved from print and radio to programmatic, social, and search, we moved with it — but we never let the tools replace the instinct. ROI isn’t a report; it’s a relationship that compounds.',
    tag: 'ROI is a relationship that compounds.',
  },
  {
    key: '04 — AI discovery',
    head: 'Into the algorithm.',
    body:
      'Today, discovery doesn’t just happen in a search bar — it happens inside AI answers, generative summaries, and recommendation engines that never show a “page one” at all. We’re building ELF-CIRCLE to meet clients inside that shift, not after it.',
    tag: 'Meeting the shift inside it, not after it.',
  },
  {
    key: '05 — Today',
    head: 'Into the firm.',
    body:
      'ELF-CIRCLE INC. is the next chapter: a firm, not a freelancer; a build team, not a single service; a global ambition, not a local footprint.',
    tag: 'A firm. A build team. A global ambition.',
  },
];

export const OurStory: React.FC = () => {
  const [active, setActive] = useState(0);
  const consult = useConsult();

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/our-story" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb
              trail={[{ label: 'Home', to: '/' }, { label: 'Who We Are', to: '/who-we-are' }, 'Our Story']}
            />
          </div>

          <div style={{ padding: '40px var(--pgut) 26px', display: 'flex', flexDirection: 'column', gap: 18 }}>
            <span className="eyebrow">
              <i />
              The story arc
            </span>
            <h1 className="elf-page__h1" style={{ fontSize: 'clamp(44px,4.6vw,68px)', lineHeight: 0.98 }}>
              Our story, <span style={{ color: 'var(--acc-panel)' }}>chapter by chapter.</span>
            </h1>
          </div>

          <div
            className="elf-page__chapters"
            style={{
              display: 'grid',
              gridTemplateColumns: '340px 1fr',
              gap: 52,
              padding: '14px var(--pgut) 60px',
              alignItems: 'start',
            }}
          >
            <div
              className="elf-page__chaprail"
              style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'sticky', top: 96 }}
            >
              {CHAPTERS.map((c, i) => (
                <button
                  key={c.key}
                  className="chtab"
                  type="button"
                  aria-pressed={i === active}
                  onClick={() => setActive(i)}
                >
                  <i />
                  <span className="stagekey">{c.key}</span>
                  <span className="whead" style={{ fontSize: 17 }}>
                    {c.head}
                  </span>
                </button>
              ))}
            </div>

            <div
              style={{
                minHeight: 360,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '34px 40px',
                borderRadius: 20,
                border: '1px solid rgba(var(--panel-fg-rgb),.12)',
                background: 'rgba(var(--panel-fg-rgb),.035)',
              }}
            >
              {CHAPTERS.map((c, i) => (
                <div
                  key={c.key}
                  /* Keyed on the active index so the entrance animation replays
                     each time a different chapter is chosen. */
                  className={i === active ? 'chpane on' : 'chpane'}
                  style={{ flexDirection: 'column', gap: 20 }}
                >
                  <span className="stagekey">Chapter {c.key}</span>
                  <h2 className="elf-page__h2">{c.head}</h2>
                  <span className="wbar" style={{ width: 84 }} />
                  <p className="wbody" style={{ maxWidth: '66ch', fontSize: 17 }}>
                    {c.body}
                  </p>
                  <span className="wtag">{c.tag}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/the-trifecta')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Next</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  The Trifecta
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Three pillars, one filter.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <button
              type="button"
              className="nextlink"
              onClick={() => consult?.open({ source: 'ourstory.consult', title: 'Book a consultation' })}
            ><div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">Start here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Book a consultation
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Thirty minutes, no deck required.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default OurStory;
